import type { StoreApi, UseBoundStore } from 'zustand';
import { getCurrencyByCode } from '@/config/currencies';
import { getCookie } from '@/lib/config/cookies';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

interface CurrencyState {
  currency: string;
  setCurrency: (code: string) => void;
}

function safeLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    const ls = window.localStorage;
    if (ls == null || typeof ls.getItem !== 'function') return null;
    return ls;
  } catch {
    // SILENT-CATCH: localStorage access can throw (Safari private mode, exotic embeds).
    return null;
  }
}

function getPersistedCurrency(): string {
  const ls = safeLocalStorage();
  if (ls === null) return 'USD';
  // Explicit user choice takes priority
  const stored = ls.getItem('preferred-currency');
  if (stored !== null && stored.length > 0) {
    if (getCurrencyByCode(stored) !== undefined) return stored;
    ls.removeItem('preferred-currency');
  }
  // Fall back to IP-detected currency from middleware cookie
  const detected = getCookie('DETECTED_CURRENCY');
  if (detected !== undefined && detected.length > 0) {
    if (getCurrencyByCode(detected) !== undefined) return detected;
  }
  return 'USD';
}

export const useCurrencyStore: UseBoundStore<StoreApi<CurrencyState>> =
  createTrackedStore<CurrencyState>('useCurrencyStore')((set) => ({
    currency: getPersistedCurrency(),
    setCurrency: (code: string): void => {
      if (getCurrencyByCode(code) === undefined) return;
      const storage = safeLocalStorage();
      if (storage !== null && typeof storage.setItem === 'function') {
        storage.setItem('preferred-currency', code);
      }
      set({ currency: code });
    },
  }));
